import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getAvatar, saveAvatar } from "../redux/playerInfoSlice";
import { AVATARS } from "../utilities/avatars";
import "./Settings.css"; // same window/tab/button chrome as the Settings page
import "./Customize.css";

const TABS = ["Profile Picture", "Trophies"];

// Customize window (SideMenu → Customize). Same window format as Settings: title bar,
// tab rail on the left, content on the right. One window-level Save (bottom right)
// commits the avatar selection; the selection is kept across tab switches so saving
// from the Trophies tab works too.
const Customize = () => {
    const dispatch = useDispatch();

    const [activeTab, setActiveTab] = useState("Profile Picture");

    const savedAvatar = useSelector((state) => state.playerInfo.avatar);
    const [selected, setSelected] = useState(savedAvatar); // avatar id, null = default "P"
    const [dirty, setDirty] = useState(false);
    const [status, setStatus] = useState(null); // { type: "ok" | "error", text }
    const [saving, setSaving] = useState(false);

    // Pull the saved pick, then mirror it into the local selection once it lands —
    // unless the user has already clicked something (their pick wins over the fetch).
    useEffect(() => { dispatch(getAvatar()); }, [dispatch]);
    useEffect(() => {
        if (!dirty) setSelected(savedAvatar);
    }, [savedAvatar, dirty]);

    const pick = (id) => {
        setSelected(id);
        setDirty(true);
        setStatus(null);
    };

    const handleSave = async () => {
        setStatus(null);
        setSaving(true);
        try {
            await dispatch(saveAvatar(selected)).unwrap();
            setDirty(false);
            setStatus({ type: "ok", text: "Profile picture saved." });
        } catch (err) {
            setStatus({ type: "error", text: typeof err === "string" ? err : "Failed to save." });
        } finally {
            setSaving(false);
        }
    };

    const renderProfilePicture = () => (
        <div className="settings-panel">
            <div className="settings-panel-header">
                <h2>Profile Picture</h2>
            </div>
            <p className="settings-panel-desc">
                Choose the picture shown on your seat at the table.
            </p>
            <hr className="settings-divider" />
            <div className="customize-avatar-grid">
                {/* Default option: no picture — the plain "P" circle. */}
                <button
                    type="button"
                    className={`customize-avatar-option ${selected == null ? "selected" : ""}`}
                    onClick={() => pick(null)}
                >
                    <span className="customize-avatar-circle customize-avatar-default">P</span>
                    <span className="customize-avatar-label">Default</span>
                </button>
                {AVATARS.map((a) => (
                    <button
                        type="button"
                        key={a.id}
                        className={`customize-avatar-option ${selected === a.id ? "selected" : ""}`}
                        onClick={() => pick(a.id)}
                    >
                        {/* object-fit:cover in a circle = centered crop touching the
                            rectangle's shorter edges — every pick renders the same size. */}
                        <img className="customize-avatar-circle" src={a.src} alt={a.label} />
                        <span className="customize-avatar-label">{a.label}</span>
                    </button>
                ))}
            </div>
        </div>
    );

    const renderTrophies = () => (
        <div className="settings-panel">
            <div className="settings-panel-header">
                <h2>Trophies</h2>
            </div>
            <p className="settings-panel-desc">
                Trophies are coming soon.
            </p>
            <hr className="settings-divider" />
        </div>
    );

    return (
        <div className="settings-page">
            <div className="settings-window">
                <div className="settings-titlebar">
                    <span className="settings-titlebar-gear">&#9998;</span>
                    <h1>Customize</h1>
                </div>
                <div className="settings-body">
                    <nav className="settings-tabs">
                        {TABS.map((tab) => (
                            <button
                                key={tab}
                                className={`settings-tab ${activeTab === tab ? "active" : ""}`}
                                onClick={() => setActiveTab(tab)}
                            >
                                {tab}
                            </button>
                        ))}
                    </nav>
                    <div className="settings-content customize-content">
                        {activeTab === "Trophies" ? renderTrophies() : renderProfilePicture()}
                        {status && (
                            <p className={status.type === "ok" ? "settings-msg-ok" : "settings-msg-error"}>
                                {status.text}
                            </p>
                        )}
                        <div className="settings-actions settings-actions-right">
                            <button
                                className="settings-action-btn"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Customize;
